<template>
  <div>
    <a-row :gutter="16">
      <a-col :span="5" class="scroll-spy">
        <a-affix :offset-top="100">
          <a-anchor :offset-top="100" @click="e => e.preventDefault()">
            <a-anchor-link href="#detail-product" :title="$t('product.product_details')" />
            <a-anchor-link href="#photo-and-video" :title="$t('product.photo_video')" />
            <a-anchor-link href="#warehouse-and-inventory" :title="$t('product.inventory')" />
            <a-anchor-link href="#type-and-price" :title="$t('product.product_type_and_price')" />
            <a-anchor-link href="#weight-and-shipping" :title="$t('product.weight_product_shipping')" />
            <a-anchor-link href="#channel-publication" :title="$t('product.channel')" />
          </a-anchor>
        </a-affix>
      </a-col>
      <a-col :span="19">
        <a-form-model
          ref="formProduct"
          class="product-form"
          :model="form"
          :rules="rules"
        >
          <DetailProduct
            v-model="form.detail"
            :loading="isLoading"
            :categories="categories"
            :brands="brands"
            class="mb-4"
            @title="onChangeTitle"
            @validate="onValidateForm"
          />
          
          <PhotoAndVideo
            v-model="form.images"
            :load="isLoading"
            :business-id="businessId"
            :is-trigger-upload="isTriggerUpload"
            class="mb-4"
            @upload="onTriggerUpload"
            @validate="onValidateForm"
          />

          <WarehouseAndInventories
            v-model="form.inventories"
            :loading="isLoading"
            :catalogs="form.catalogs.items"
            :warehouses="warehouses"
            :default-item="defaultItem"
            class="mb-4"
            @validate="onValidateForm"
          />
          
          <TypeAndPrice
            v-model="form.catalogs"
            :loading="isLoading"
            :images="form.images"
            :inventories="form.inventories"
            :default-item="defaultItem"
            :current-uom="currentUom"
            :uom="uom"
            :business-id="businessId"
            :currency="currency"
            class="mb-4"
            @addUom="isUomModalVisible = true"
            @changeUom="onConfirmChangeUom"
            @title="onChangeTitle"
            @upload="onTriggerUpload"
            @validate="onValidateForm"
          />

          <WeightAndShipping
            v-model="form.units"
            :loading="isLoading"
            :uom="uom"
            class="mb-4"
            @changePhysical="onChangePhysical"
            @validate="onValidateForm"
            @addUom="isUomModalVisible = true"
          />
          
          <ChannelPublication
            v-model="form.channels"
            :loading="isLoading"
            :business-id="businessId"
            :brand-id="form.detail.brand_id"
            :channel-list="channels"
            :channel-categories="channelCategories"
            @add="addChannelAttribute"
          />
          
          <div class="product-form-footer">
            <a-button
              type="primary"
              size="large"
              class="w-120"
              ghost
              :disabled="isLoading"
              @click="onCancel"
            >
              {{ $t('fulfillment.cancel') }}
            </a-button>
            <a-button
              type="primary"
              size="large"
              class="w-120 ml-4"
              :loading="isLoading"
              :disabled="isLoading"
              @click.prevent="onSubmit"
            >
              {{ $t('fulfillment.save') }}
            </a-button>
          </div>
        </a-form-model>
      </a-col>
    </a-row>

    <a-modal
      :visible="isUomModalVisible"
      :closable="false"
      :footer="null"
      centered
      @cancel="onCloseUomModal"
    >
      <a-form-model ref="formUom" :model="frm" :rules="rulesUom">
        <a-form-model-item prop="newUom" class="mb-0">
          <a-input-group compact>
            <a-input
              v-model="frm.newUom"
              :placeholder="$t('product.unitNamePlaceholder')"
              size="large"
              style="width: 70%; text-transform: uppercase"
              :max-length="15"
            />
            <a-button type="primary" size="large" style="width: 30%" @click="onAddUom">
              {{ $t('utils.add') }}
            </a-button>
          </a-input-group>
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </div>
</template>

<script>
import DetailProduct from './DetailProduct.vue'
import PhotoAndVideo from './PhotoAndVideo.vue'
import TypeAndPrice from './TypeAndPrice/index.vue'
import WeightAndShipping from './WeightAndShipping.vue'
import WarehouseAndInventories from './WarehouseAndInventories.vue'
import ChannelPublication from './ChannelPublication.vue'

import { getBusinessChannel } from '@/api/business'
import { getWarehouses } from '@/api/warehouse'
import { getCategoryList, publishProduct, getProductDetail } from '@/api/product'

import debounce from 'lodash/debounce'

export default {
  name: 'ProductForm',
  components: {
    DetailProduct,
    PhotoAndVideo,
    TypeAndPrice,
    WeightAndShipping,
    WarehouseAndInventories,
    ChannelPublication,
  },
  data() {

    const DEFAULT_ITEM = {
      id: 1,
      factory_sku: null,
      images: [],
      option: null,
      price: {
        currency: 'IDR',
        value: 0,
        min_qty: 1,
        price_qty: 1,
        uom: 'PCS',
        id: null,
        formula_id: null,
        product_variables: [],
      },
      sku: null,
      barcode: null,
      status: true,
      title: null,
      loading: false,
      msg: '',
      availability: 0,
      on_hand: 0,
      sku_exist: 0,
    }

    const DEFAULT_UNIT = {
      unit: 'PCS',
      numerator: 1,
      denumerator: 1,
      length: null,
      length_unit: 'cm',
      width: null,
      width_unit: 'cm',
      height: null,
      height_unit: 'cm',
      weight: null,
      weight_unit: 'g',
    }

    return {
      isLoading: false,
      isUomModalVisible: false,
      isTriggerUpload: false,
      currentUom: 'PCS',
      product: {},
      frm: { newUom: '' },
      form: {
        detail: {
          title: '',
          category_id: null,
          category: {},
          brand_id: null,
          condition: 'NEW',
          imported: false,
          long_description: '',
          physical: true,
        },
        images: [],
        catalogs: {
          is_variant: false,
          attributes: {},
          options: [],
          items: [JSON.parse(JSON.stringify(DEFAULT_ITEM))],
          bundling: {
            bundling_type: null,
            bundles: [],
          },
        },
        units: [JSON.parse(JSON.stringify(DEFAULT_UNIT))],
        inventories: [],
        channels: [],
      },
      defaultItem: JSON.parse(JSON.stringify(DEFAULT_ITEM)),
      defaultUnit: JSON.parse(JSON.stringify(DEFAULT_UNIT)),
      categories: [],
      brands: [],
      uom: ['PCS', 'DZN', 'CTN'],
      warehouses: [],
      channels: [],
      rawChannels: [],
      channelAttribute: [],
      supportedChannels: ['b2b', 'distributor', 'distributor_store', 'fund_raiser', 'shopee', 'store', 'tiktok', 'tokopedia', 'webcommerce'],
      channelList: ['b2b', 'distributor', 'distributor_store', 'shopee', 'tiktok', 'tokopedia'],
      channelCategories: {},
      relationMp: [],
    }
  },
  computed: {
    businessId() {
      return this.$store.state.user.restriction_base === 'Warehouse'
        ? Object.keys(this.$store.getters['user/businessListObjectByKey'])[0]
        : this.business?.business_id || (this.$route.query.business_id
        ? String(this.$route.query.business_id)
        : null)
    },
    currency() {
      return this.$store.getters['user/businessListObjectByKey'][this.businessId]?.currency?.currency_code || 'IDR'
    },
    rules() {
      return {
        detail: {
          title: [{ required: true, message: this.$t('product.validateNameRequired'), trigger: 'blur', whitespace: true }],
          category_id: [{ type: 'array', required: true, message: this.$t('product.validateCategoryRequired'), trigger: 'change' }],
          long_description: [{ required: true, message: this.$t('product.validateDescriptionRequired'), trigger: 'blur' }],
        },
        images: [{ type: 'array', required: true, message: this.$t('product.validateImageRequired'), trigger: 'change' }],
        inventories: [{ type: 'array', required: true, message: this.$t('product.validateInventoryRequired'), trigger: 'change' }],
      }
    },
    rulesUom() {
      return {
        newUom: [{ validator: this.validateUom, trigger: 'blur' }],
      }
    },
  },
  watch: {
    businessId() {
      this.fetchWarehouse()
      this.fetchChannels()
      this.fetchCategories()
      this.fetchBrands()
    },
  },
  mounted() {
    this.form.catalogs.items = this.form.catalogs.items.map(item => ({
      ...item,
      price: {
        ...item.price,
        currency: this.currency,
      },
    }))
    this.defaultItem = {
      ... this.defaultItem,
      price: {
        ...this.defaultItem.price,
        currency: this.currency,
      },
    }
    this.fetchWarehouse()
    this.fetchChannels()
    this.fetchCategories()
    this.fetchBrands()
  },
  methods: {
    async imageUrlToFile(imageUrl, fileName, mimeType) {
      const response = await fetch(imageUrl)
      const blob = await response.blob()
      return new File([blob], fileName, { type: mimeType || blob.type })
    },
    validateUom(rule, value, callback) {
      if (value.trim() === '') {
        callback(new Error(this.$t('product.validateUnitRequired')))
      } else if (value.length < 2) {
        callback(new Error(this.$t('product.validateUnitMin')))
      } else if (this.uom.includes(value?.toUpperCase())) {
        callback(new Error(this.$t('product.validateUnitExist')))
      } else {
        callback()
      }
    },
    fetchWarehouse: debounce(async function() {
      const { data: { data } } = await getWarehouses({ business_id: this.businessId })
      this.warehouses = (
        data?.data && this.$route.query.warehouse_id
          ? data.data.filter(warehouse => warehouse.warehouse_id === this.$route.query.warehouse_id)
          : data.data
      ) || []
    }, 500),
    fetchCategories: debounce(async function() {
      const { data } = await getCategoryList({ params: { business_id: this.businessId } })
      this.categories = data?.master || []
    }, 500),
    fetchBrands: debounce(async function() {
      const data = await this.$store.dispatch('products/GETBRANDS', { business_id: this.businessId })
      this.brands = data
    }, 500),
    fetchChannels: debounce(async function() {
      this.channels = []
      const { data: { data } } = await getBusinessChannel(this.businessId)
      data?.channels?.forEach(channel => {
        const isSupportedChannel = this.supportedChannels.find(ch => new RegExp(`^${ch}(_.*)?$`).test(channel.code))
        if(isSupportedChannel && channel.status.toLowerCase() === 'connected') {
          const findChannel = this.$store.state.business.listChannels.find(item => item.id == channel.id && item.sales_channel.code === channel.code)
          this.channels.push({
            ...channel,
            sales: findChannel?.sales_channel?.id || '',
            code: channel.code === 'tiktok_tokopedia' ? 'tiktok' : channel.code,
            groupCode: channel.code,
          })
          this.rawChannels.push({
            ...channel,
            sales: findChannel?.sales_channel?.id || '',
            code: channel.code === 'tiktok_tokopedia' ? 'tiktok' : channel.code,
            groupCode: channel.code,
          })
        }
      })
    }, 500),
    onChangeTitle() {
      this.form.catalogs.items = this.form.catalogs.items.map(item => ({
        ...item,
        title: this.form.catalogs.is_variant && item.option
          ? this.form.detail.title + ' - ' + Object.values(item.option).join(', ')
          : this.form.detail.title,
      }))
    },
    onCloseUomModal() {
      if (this.$refs.formUom) {
        this.$refs.formUom.resetFields()
      }

      this.frm.newUom = ''
      this.isUomModalVisible = false
    },
    onAddUom() {
      this.$refs.formUom.validate((valid) => {
        if (valid) {
          this.uom.push(this.frm.newUom.toUpperCase())
          this.frm.newUom = ''
          this.isUomModalVisible = false
        }
      })
    },
    onConfirmChangeUom(uom) {
      if (this.form.units.length > 1) {
        this.$confirm({
          icon: () => null,
          title: this.$t('product.changeUnitTitle'),
          content: this.$t('product.changeUnitContent', { old: this.currentUom, new: uom.value }),
          okText: this.$t('product.changeUnitOk'),
          onOk: () => this.onChangeUom(uom.value),
          onCancel: (close) => {
            this.form.catalogs.items[uom.index].price.uom = this.currentUom
            close()
          },
        })
      } else {
        this.onChangeUom(uom.value)
      }
    },
    onChangePhysical(value) {
      this.form.detail.physical = value
    },
    onChangeUom(value) {
      const defaultUnit = JSON.parse(JSON.stringify(this.defaultUnit))
      this.currentUom = value
      this.form.units = [{ ...defaultUnit, unit: value }]
      this.form.catalogs.items = this.form.catalogs.items.map(item => ({
        ...item,
        price: {
          ...item.price,
          uom: value,
        },
      }))
      this.form.inventories = this.form.inventories.map(inventory => ({
        ...inventory,
        catalogs: inventory.catalogs.map(catalog => ({
          ...catalog,
          uom: value,
        })),
      }))

      if (this.form.catalogs.bundling.bundles.length) {
        this.form.catalogs.bundling.bundles = this.form.catalogs.bundling.bundles.map(bundle => ({
          ...bundle,
          uom: value,
        }))
      }
    },
    onTriggerUpload(value) {
      this.isTriggerUpload = value
    },
    onValidateForm(field) {
      this.$refs.formProduct.validateField(field)
    },
    onCancel() {
      const redirectUrl = this.$route.query.redirect_url

      if (redirectUrl) {
        return this.$router.push(redirectUrl)
      }

      return this.$router.push({
        name: 'product.master',
        query: {
          workspace_id: this.$route.query.workspace_id,
          business_id: this.$route.query.business_id,
        },
      })
    },
    addChannelAttribute(value) {
      this.channelAttribute = value
    },
    findCategory(lists, matched) {
      let length = lists.length
      let result = null
      for (let i = 0; i < length; i++) {
        if (lists[i].id == matched) {
          const { id, name } = lists[i]
          return { id, name }
        } else if (lists[i].children) {
          result = this.findCategory(lists[i].children, matched)
          if (result.id) return result
        }
      }
      return {}
    },
    onSubmit() {
      this.$refs.formProduct.validate(valid => {
        if (valid) {
          if (!this.form.catalogs.is_variant && this.form.catalogs.bundling.bundling_type) {
            if (!this.form.catalogs.bundling.bundles.length) {
              this.$message.error(this.$t('product.invalidData'), 5)
              return
            } else {
              const isHaveMessage = this.form.catalogs.bundling.bundles.some(item => item.message)
              if (isHaveMessage) {
                this.$message.error(this.$t('product.invalidData'), 5)
                return
              }
            }
          }

          this.isLoading = true
          this.onSaveProduct()
        } else {
          this.$message.error(this.$t('product.invalidData'), 5)
        }
      })
    },
    onSaveProduct: debounce(async function() {
      const { images, ...data } = this.form

      let count = 0
      let channels = []
      data.channels?.forEach(channel => {
        const isChannelList = this.channelList.find(ch => new RegExp(`^${ch}(_.*)?$`).test(channel.code))
        if(isChannelList) count++
        channels.push(channel.id)
      })

      this.$store.commit('marketplace/SET_EXISTING_CHANNELS', [])
      this.$store.commit('marketplace/SET_COUNT', count)

      await publishProduct({
        business_id: this.businessId,
        user_id: this.$store.state.user.id,
        data: {
          ...data,
          detail: {
            ...data.detail,
            category: this.findCategory(this.categories, Number(data.detail.category_id[data.detail.category_id.length - 1])),
          },
          channels,
        },
        images,
      })
      .then(async ({ data: response }) => {

        if (response?.status === 'success') {
          try {
            const { data: { data: productDetail } } = await getProductDetail({
              id: response.product_id,
              params: { business_id: this.businessId },
            })

            if (productDetail?.images) {
              data.images = await Promise.all(productDetail.images.map(async (image, index) => {
                if(!image.file) {
                  await this.imageUrlToFile(image.url, `image-${response.product_id}-${index}.jpg`, 'image/jpeg')
                    .then(imageFile => image.file = imageFile)
                    .catch(() => image.file = null)
                }
                return image
              }))
            }
            if (productDetail?.catalogs?.length) {
              let responseCatalogs = [...productDetail.catalogs]
              let currentCatalogItems = [...data.catalogs.items]
              currentCatalogItems.map(catalog => {
                const matchedCatalog = responseCatalogs.find(c => c.sku === catalog.sku)
                if (matchedCatalog) {
                  catalog.id = matchedCatalog.id
                }
                return catalog
              })
              data.catalogs.items = currentCatalogItems
            }
          } catch (e) {
            console.error(e)
          }

          this.$message.success(this.$t('product.saveSuccess'), 5)
          const { workspace_id, business_id, redirect_url } = this.$route.query
          this.$router.push({ path: redirect_url || '/product', query: { workspace_id, business_id } })
            .then(async() => {

              if(+this.$store.state.marketplace.count > 0) {
                const brand_id = (
                  this.brands.length && data.detail.brand_id
                    ? this.brands.find(brand => brand.id === data.detail.brand_id)?.title
                    : data.detail.brand_id
                ) || ''
                const modelChannels = this.rawChannels.filter(ch => channels.includes(ch.id))

                const payload = {
                  ...data,
                  channels: modelChannels,
                  brand_id,
                  business_id: this.businessId,
                  user_id: this.$store.state.user.id,
                  attributes: this.channelAttribute,
                  relations: this.relationMp,
                  id: response.product_id,
                  units: data.units.map(item => ({
                    package: {
                      ...item,
                    },
                  })),
                  status: 'ACTIVE',
                }


                await this.$store.commit('marketplace/SET_MODEL', payload)
              }
            })
        } else {
          const message = response?.message ? response?.message : this.$t('product.saveFailed')
          this.$message.error(message, 5)
        }
      })
      .catch(() => this.$message.error(this.$t('product.saveFailed'), 5))
      .finally(() => this.isLoading = false)
    }, 1000),
  },
}
</script>

<style lang="scss">
.w-120 {
  width: 120px;
}

.product-form {
  .ant-form-item {
    margin-bottom: 19px !important;

    &.ant-form-item-with-help {
      margin-bottom: 0 !important;
    }
  }

  &-footer {
    margin-top: 25px;
    margin-bottom: 50px;
    padding-top: 25px;
    border-top: 1px solid #e4e9f0;
    text-align: right;
  }
}

.scroll-spy .ant-affix {
  top: 100px !important;
}

.btn-uom {
  margin: 4px;
  padding: 6px 0;
  font-size: 12px;
  line-height: 1.25;
  text-align: center;
  color: white;
  background-color: #FF0A54;
  border-radius: 4px;
  cursor: pointer;
}

.ant-anchor-link-title {
  line-height: normal !important;
  white-space: inherit !important;
}

.addon-before {
  width: 80px;
  height: 40px;
  padding: 8px 0;
  font-size: 15px;
  background-color: #fafafa;
  border: 1px solid #d9d9d9;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  text-align: center;
}
</style>